import { useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { ChevronDown, Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useListHazardRiskReductionSteps,
  useListRiskReductionMeasures,
  type RiskOutGenerationMode,
  type RiskReductionMeasureType,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { CreateHazardRiskReductionStepFormValues } from './CreateHazardRiskReductionStepForm.schema'
import {
  RiskInjurySelect,
  RiskFrequencySelect,
  RiskAvoidanceSelect,
  RiskLikelihoodSelect,
} from './RiskDimensionSelects'
import {
  getRrmHierarchyTypesWarning,
  groupRiskReductionMeasuresByType,
  RRM_HIERARCHY_TYPE_ORDER,
  type RrmHierarchyTypesWarning,
} from './rrmHierarchyTypes'
import { useHazardRiskReductionStepRiskInCap } from './useHazardRiskReductionStepRiskInCap'

export type HazardRiskReductionStepFormFieldsProps = {
  form: UseFormReturn<CreateHazardRiskReductionStepFormValues>
  hazardId: string
  complianceId: string
  excludeStepId?: string
  isGenerating: boolean
  hasAllRiskOutDimensionsSelected: boolean
  onGenerateRiskOut: (mode: RiskOutGenerationMode) => void
}

function getHierarchyWarningMessageKey(
  warning: RrmHierarchyTypesWarning
): string {
  if (warning === 'skippedIsdAndScp') {
    return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningSkippedIsdAndScp'
  }
  if (warning === 'isdAfterLater') {
    return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningIsdAfterLater'
  }
  return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningScpAfterResidual'
}

function WriteJustificationsMenuItem({
  disabled,
  disabledTooltip,
  label,
  onSelect,
}: {
  disabled: boolean
  disabledTooltip: string
  label: string
  onSelect: () => void
}) {
  const menuItem = (
    <DropdownMenuItem disabled={disabled} onClick={onSelect}>
      {label}
    </DropdownMenuItem>
  )

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='w-full cursor-not-allowed'>{menuItem}</div>
        </TooltipTrigger>
        <TooltipContent side='right'>{disabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return menuItem
}

export function HazardRiskReductionStepFormFields({
  form,
  hazardId,
  complianceId,
  excludeStepId,
  isGenerating,
  hasAllRiskOutDimensionsSelected,
  onGenerateRiskOut,
}: HazardRiskReductionStepFormFieldsProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: riskReductionMeasures = [] } =
    useListRiskReductionMeasures(complianceId)
  const { data: hazardSteps = [] } = useListHazardRiskReductionSteps(hazardId)
  const { riskInCap, isRiskOutCapPending } =
    useHazardRiskReductionStepRiskInCap({
      hazardId,
      complianceId,
      excludeStepId,
    })

  const selectedRrmId = form.watch('riskReductionMeasureId')

  const groupedMeasures = useMemo(
    () => groupRiskReductionMeasuresByType(riskReductionMeasures),
    [riskReductionMeasures]
  )

  const riskReductionMeasureItems = useMemo(
    () =>
      riskReductionMeasures.map((rrm) => ({
        value: rrm.id,
        label: `${rrm.name} (${rrm.type})`,
      })),
    [riskReductionMeasures]
  )

  const existingTypes = useMemo(() => {
    const currentSequence = excludeStepId
      ? hazardSteps.find((step) => step.id === excludeStepId)?.sequence
      : undefined

    return hazardSteps
      .filter((step) => step.id !== excludeStepId)
      .filter((step) =>
        currentSequence != null ? step.sequence < currentSequence : true
      )
      .map((step) => step.riskReductionMeasure.type)
  }, [excludeStepId, hazardSteps])

  const selectedType = useMemo(() => {
    const selectedMeasure = riskReductionMeasures.find(
      (rrm) => rrm.id === selectedRrmId
    )
    return selectedMeasure?.type
  }, [riskReductionMeasures, selectedRrmId])

  const hierarchyWarning = getRrmHierarchyTypesWarning(
    selectedType,
    existingTypes
  )

  const showHierarchyWarning = hierarchyWarning != null

  const isRiskOutSelectDisabled = isGenerating || isRiskOutCapPending

  const getMeasureTypeLabel = (type: RiskReductionMeasureType) =>
    t(`riskReductionMeasuresLibraryPage.form.measureTypes.${type}`)

  let generatingLabel = t('hazardsPage.hazardDetail.riskFlow.suggestRiskOut')
  if (isGenerating) {
    generatingLabel = t('hazardsPage.hazardDetail.riskFlow.generatingRiskOut')
  }

  return (
    <div className='flex flex-col space-y-6'>
      <FormField
        control={form.control}
        name='riskReductionMeasureId'
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(
                'hazardsPage.hazardDetail.riskFlow.selectRiskReductionMeasure'
              )}{' '}
              *
            </FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => {
                if (value) {
                  field.onChange(value)
                }
              }}
              items={riskReductionMeasureItems}
            >
              <FormControl>
                <SelectTrigger className='w-full' id='rrm-step-measure'>
                  <SelectValue
                    placeholder={t(
                      'hazardsPage.hazardDetail.riskFlow.selectRiskReductionMeasurePlaceholder'
                    )}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {RRM_HIERARCHY_TYPE_ORDER.map((type) => {
                  const measuresForType = groupedMeasures.get(type) ?? []

                  return (
                    <SelectGroup key={type}>
                      <SelectLabel className='font-semibold text-foreground'>
                        {getMeasureTypeLabel(type)}
                      </SelectLabel>
                      {measuresForType.map((rrm) => (
                        <SelectItem key={rrm.id} value={rrm.id}>
                          {rrm.name} ({rrm.type})
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                })}
              </SelectContent>
            </Select>
            {showHierarchyWarning && hierarchyWarning != null && (
              <Alert variant='warning'>
                <AlertDescription>
                  {t(getHierarchyWarningMessageKey(hierarchyWarning))}
                </AlertDescription>
              </Alert>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className='space-y-3'>
        <div className='flex items-center gap-2'>
          <p className='text-sm font-medium'>
            {t('hazardsPage.hazardDetail.riskFlow.riskOut')}
          </p>
          {selectedRrmId ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={isRiskOutSelectDisabled}
                >
                  {isGenerating ? (
                    <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Sparkles className='mr-1.5 h-3.5 w-3.5' />
                  )}
                  {generatingLabel}
                  {!isGenerating ? (
                    <ChevronDown className='ml-1 h-3.5 w-3.5' />
                  ) : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                <DropdownMenuItem
                  onClick={() => onGenerateRiskOut('dimensions')}
                >
                  {t('hazardsPage.hazardDetail.riskFlow.suggestRiskOut')}
                </DropdownMenuItem>
                <WriteJustificationsMenuItem
                  disabled={!hasAllRiskOutDimensionsSelected}
                  disabledTooltip={t(
                    'hazardsPage.hazardDetail.riskFlow.writeJustificationsDisabledTooltip'
                  )}
                  label={t(
                    'hazardsPage.hazardDetail.riskFlow.writeJustifications'
                  )}
                  onSelect={() => onGenerateRiskOut('justifications')}
                />
                <DropdownMenuItem onClick={() => onGenerateRiskOut('both')}>
                  {t('hazardsPage.hazardDetail.riskFlow.doBoth')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutInjury'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.injury')}
                  </FormLabel>
                  <FormControl>
                    <RiskInjurySelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.injury}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutInjuryJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutFrequency'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.frequency')}
                  </FormLabel>
                  <FormControl>
                    <RiskFrequencySelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.frequency}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutFrequencyJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutAvoidance'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.avoidance')}
                  </FormLabel>
                  <FormControl>
                    <RiskAvoidanceSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.avoidance}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutAvoidanceJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutLikelihood'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.likelihood')}
                  </FormLabel>
                  <FormControl>
                    <RiskLikelihoodSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.likelihood}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutLikelihoodJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
